<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import RealEstateCommissionPlanManagerObservation from './RealEstateCommissionPlanManagerObservation.vue';

const props = defineProps({
  form: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  managerObservation: { type: String, default: '' },
  netCents: { type: Number, default: 0 },
});

const emit = defineEmits(['update', 'changeModality', 'changeCount']);
const { t } = useI18n();
const netAmount = computed(() => (props.netCents / 100).toFixed(2));
const methods = computed(() => [
  {
    id: 'especie',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PAYMENT_METHODS.CASH'),
  },
  {
    id: 'deposito',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PAYMENT_METHODS.DEPOSIT'),
  },
  {
    id: 'ted_doc',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PAYMENT_METHODS.TED_DOC'),
  },
  { id: 'pix', label: t('REAL_ESTATE_COMMISSION_PLAN.PAYMENT_METHODS.PIX') },
  {
    id: 'cheque',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PAYMENT_METHODS.CHEQUE'),
  },
]);
</script>

<template>
  <LaisCard
    :title="t('REAL_ESTATE_COMMISSION_PLAN.EDITOR.TITLE')"
    prefix-icon="i-lucide-pencil-line"
  >
    <div class="grid gap-4 lg:grid-cols-3">
      <LaisField
        :label="t('REAL_ESTATE_COMMISSION_PLAN.FIELDS.GROSS')"
        :error="errors.grossAmount"
        required
      >
        <input
          :value="form.grossAmount"
          inputmode="decimal"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          :placeholder="t('REAL_ESTATE_COMMISSION_PLAN.PLACEHOLDERS.MONEY')"
          @input="emit('update', 'grossAmount', $event.target.value)"
        />
      </LaisField>
      <LaisField
        :label="t('REAL_ESTATE_COMMISSION_PLAN.FIELDS.DEDUCTION')"
        :error="errors.deduction"
      >
        <input
          :value="form.deduction"
          inputmode="decimal"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          :placeholder="t('REAL_ESTATE_COMMISSION_PLAN.PLACEHOLDERS.MONEY')"
          @input="emit('update', 'deduction', $event.target.value)"
        />
      </LaisField>
      <LaisField :label="t('REAL_ESTATE_COMMISSION_PLAN.FIELDS.NET')">
        <input
          :value="netAmount"
          readonly
          class="min-h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-3 text-sm"
        />
      </LaisField>
      <LaisField
        :label="t('REAL_ESTATE_COMMISSION_PLAN.FIELDS.METHOD')"
        :error="errors.paymentMethod"
        required
      >
        <select
          :value="form.paymentMethod"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @change="emit('update', 'paymentMethod', $event.target.value)"
        >
          <option value="">
            {{ t('REAL_ESTATE_COMMISSION_PLAN.PAYMENT_METHODS.SELECT') }}
          </option>
          <option v-for="method in methods" :key="method.id" :value="method.id">
            {{ method.label }}
          </option>
        </select>
      </LaisField>
      <LaisField
        class="lg:col-span-2"
        :label="t('REAL_ESTATE_COMMISSION_PLAN.FIELDS.NOTES')"
      >
        <textarea
          :value="form.notes"
          rows="3"
          class="w-full rounded-lais border border-lais-border bg-white p-3 text-sm"
          :placeholder="t('REAL_ESTATE_COMMISSION_PLAN.PLACEHOLDERS.NOTES')"
          @input="emit('update', 'notes', $event.target.value)"
        />
      </LaisField>
    </div>
  </LaisCard>

  <LaisCard
    class="mt-5"
    :title="t('REAL_ESTATE_COMMISSION_PLAN.EDITOR.MODALITY_TITLE')"
    prefix-icon="i-lucide-split"
  >
    <div class="flex flex-wrap gap-4">
      <label
        class="flex items-center gap-2 text-sm font-semibold text-lais-text"
      >
        <input
          type="radio"
          name="commission-modality"
          value="integral"
          :checked="form.modality === 'integral'"
          @change="emit('changeModality', 'integral')"
        />
        {{ t('REAL_ESTATE_COMMISSION_PLAN.MODALITIES.INTEGRAL') }}
      </label>
      <label
        class="flex items-center gap-2 text-sm font-semibold text-lais-text"
      >
        <input
          type="radio"
          name="commission-modality"
          value="partes"
          :checked="form.modality === 'partes'"
          @change="emit('changeModality', 'partes')"
        />
        {{ t('REAL_ESTATE_COMMISSION_PLAN.MODALITIES.INSTALLMENTS') }}
      </label>
    </div>
    <LaisField
      v-if="form.modality === 'partes'"
      class="mt-4 max-w-xs"
      :label="t('REAL_ESTATE_COMMISSION_PLAN.FIELDS.INSTALLMENT_COUNT')"
      required
    >
      <input
        :value="form.installments.length"
        type="number"
        min="1"
        class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
        @input="emit('changeCount', $event.target.value)"
      />
    </LaisField>
  </LaisCard>

  <RealEstateCommissionPlanManagerObservation
    :observation="managerObservation"
  />
</template>
